import { useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { PERIOD_OPTIONS, PeriodKey } from "../../../../consts";
import {
  formatYMD,
  subtractDays,
  subtractMonths,
  subtractYears,
} from "../../../../utils";
import Select from "../_select";
import "./index.scss";

export interface OnPeriodChangePayload {
  period: PeriodKey;
  startDate: string | null;
  endDate: string | null;
}

interface PeriodSelectorProps {
  onPeriodChange?: (data: OnPeriodChangePayload) => void;
  period: OnPeriodChangePayload;
}

const PeriodSelector = ({ onPeriodChange, period }: PeriodSelectorProps) => {
  const today = formatYMD(new Date());

  const [selectedPeriod, setSelectedPeriod] = useState<PeriodKey>(
    period.period || "always"
  );

  const startRef = useRef<HTMLInputElement | null>(null);
  const endRef = useRef<HTMLInputElement | null>(null);

  const dropdownOptions = Object.values(PERIOD_OPTIONS).map((opt) => ({
    label: opt.title,
    value: opt.value,
  }));

  const handlePeriodSelect = (periodKey: PeriodKey) => {
    setSelectedPeriod(periodKey);

    let newStart: string | null = null;
    let newEnd: string | null = null;

    if (periodKey === "7_days") {
      newEnd = today;
      newStart = formatYMD(subtractDays(new Date(), 7));
    }

    if (periodKey === "90_days") {
      newEnd = today;
      newStart = formatYMD(subtractDays(new Date(), 90));
    }

    if (periodKey === "6_months") {
      newEnd = today;
      newStart = formatYMD(subtractMonths(new Date(), 6));
    }

    if (periodKey === "2_years") {
      newEnd = today;
      newStart = formatYMD(subtractYears(new Date(), 2));
    }

    if (periodKey === "always") {
      newStart = null;
      newEnd = null;
    }

    if (periodKey === "custom") {
      newStart = today;
      newEnd = today;
    }

    onPeriodChange?.({
      period: periodKey,
      startDate: newStart,
      endDate: newEnd,
    });
  };

  const handleDateChange = (type: "start" | "end", value: string) => {
    if (type === "start") {
      onPeriodChange?.({
        period: "custom",
        startDate: value,
        endDate: period.endDate,
      });
    } else {
      onPeriodChange?.({
        period: "custom",
        startDate: period.startDate,
        endDate: value,
      });
    }
  };

  const formatDisplay = (dateString: string | null): string => {
    if (!dateString) return "";
    const [y, m, d] = dateString.split("-");
    const date = new Date(Number(y), Number(m) - 1, Number(d));

    const month = date.toLocaleString("en-US", { month: "short" });
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month} ${day}, ${year}`;
  };

  return (
    <div className="period-selector">
      <Select
        options={dropdownOptions}
        value={selectedPeriod}
        onSelect={(value) => handlePeriodSelect(value as PeriodKey)}
        className="period-dropdown"
      />

      {selectedPeriod === "custom" && (
        <div className="custom-date-inputs">
          <div className="date-input-wrapper">
            <Form.Control
              type="date"
              ref={startRef}
              value={period.startDate ?? ""}
              onChange={(e) => handleDateChange("start", e.target.value)}
              className="date-input"
            />
            <span
              className="date-display"
              onClick={() =>
                startRef.current?.showPicker?.() || startRef.current?.click()
              }
            >
              {formatDisplay(period.startDate)}
            </span>
          </div>

          <span className="date-separator">-</span>

          <div className="date-input-wrapper">
            <Form.Control
              type="date"
              ref={endRef}
              value={period.endDate ?? ""}
              onChange={(e) => handleDateChange("end", e.target.value)}
              className="date-input"
            />
            <span
              className="date-display"
              onClick={() =>
                endRef.current?.showPicker?.() || endRef.current?.click()
              }
            >
              {formatDisplay(period.endDate)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodSelector;
